import React, { useState } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, TextInput, ScrollView, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Picker } from '@react-native-picker/picker';
import DateTimePicker from '@react-native-community/datetimepicker';

const MENTOR_PRIMARY = '#7c3aed';

const MentorScheduleModal = ({ visible, onClose, onSchedule }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [scheduledDate, setScheduledDate] = useState(new Date());
    const [duration, setDuration] = useState('60');

    const [showDatePicker, setShowDatePicker] = useState(false);
    const [dateMode, setDateMode] = useState('date'); // 'date' or 'time'

    const handleSubmit = () => {
        if (!title.trim()) {
            alert('Please provide a title for the session.');
            return;
        }

        onSchedule({
            title,
            description,
            scheduledStartTime: scheduledDate.toISOString(),
            duration: parseInt(duration),
        });

        // Reset and close
        setTitle('');
        setDescription('');
        setScheduledDate(new Date());
        onClose();
    };

    const onDateChange = (event, selectedDate) => {
        const currentDate = selectedDate || scheduledDate;
        setShowDatePicker(Platform.OS === 'ios');
        setScheduledDate(currentDate);
    };

    const showMode = (currentMode) => {
        setShowDatePicker(true);
        setDateMode(currentMode);
    };

    return (
        <Modal
            animationType="slide"
            transparent={true}
            visible={visible}
            onRequestClose={onClose}
        >
            <View style={styles.modalOverlay}>
                <View style={styles.modalContent}>
                    <View style={styles.modalHeader}>
                        <View>
                            <Text style={styles.modalTitle}>Schedule Space</Text>
                            <Text style={styles.modalSubtitle}>Plan a future mentoring session</Text>
                        </View>
                        <TouchableOpacity style={styles.closeBtn} onPress={onClose}>
                            <Ionicons name="close" size={24} color="#6B7280" />
                        </TouchableOpacity>
                    </View>

                    <ScrollView style={styles.form} showsVerticalScrollIndicator={false}>
                        <Text style={styles.label}>Space Title</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="e.g. Career Guidance Workshop"
                            placeholderTextColor="#9CA3AF"
                            value={title}
                            onChangeText={setTitle}
                        />

                        <Text style={styles.label}>Description (Optional)</Text>
                        <TextInput
                            style={[styles.input, styles.textArea]}
                            placeholder="What will you discuss?"
                            placeholderTextColor="#9CA3AF"
                            value={description}
                            onChangeText={setDescription}
                            multiline={true}
                            numberOfLines={3}
                        />

                        <Text style={styles.label}>Start Date & Time</Text>
                        <View style={styles.dateTimeRow}>
                            <TouchableOpacity style={styles.dateTimeButton} onPress={() => showMode('date')}>
                                <Ionicons name="calendar-outline" size={20} color={MENTOR_PRIMARY} />
                                <Text style={styles.dateTimeButtonText}>{scheduledDate.toLocaleDateString()}</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={styles.dateTimeButton} onPress={() => showMode('time')}>
                                <Ionicons name="time-outline" size={20} color={MENTOR_PRIMARY} />
                                <Text style={styles.dateTimeButtonText}>{scheduledDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Text>
                            </TouchableOpacity>
                        </View>

                        {showDatePicker && (
                            <DateTimePicker
                                value={scheduledDate}
                                mode={dateMode}
                                is24Hour={true}
                                display="default"
                                onChange={onDateChange}
                                minimumDate={new Date()}
                            />
                        )}

                        <Text style={styles.label}>Duration</Text>
                        <View style={styles.pickerContainer}>
                            <Picker
                                selectedValue={duration}
                                onValueChange={(itemValue) => setDuration(itemValue)}
                                style={styles.picker}
                            >
                                <Picker.Item label="30 Minutes" value="30" />
                                <Picker.Item label="45 Minutes" value="45" />
                                <Picker.Item label="60 Minutes" value="60" />
                                <Picker.Item label="90 Minutes" value="90" />
                                <Picker.Item label="120 Minutes" value="120" />
                            </Picker>
                        </View>

                        <View style={{ height: 30 }} />
                    </ScrollView>

                    <TouchableOpacity style={styles.submitButton} onPress={handleSubmit}>
                        <Text style={styles.submitButtonText}>Schedule Space</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'flex-end',
    },
    modalContent: {
        backgroundColor: '#fff',
        borderTopLeftRadius: 32,
        borderTopRightRadius: 32,
        padding: 24,
        maxHeight: '90%',
    },
    modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 24,
    },
    closeBtn: {
        padding: 4,
        backgroundColor: '#F3F4F6',
        borderRadius: 12,
    },
    modalTitle: {
        fontSize: 22,
        fontWeight: '800',
        color: '#111827',
    },
    modalSubtitle: {
        fontSize: 14,
        color: '#6B7280',
        marginTop: 2,
    },
    form: {
        marginBottom: 10,
    },
    label: {
        fontSize: 14,
        fontWeight: '700',
        color: '#374151',
        marginBottom: 8,
        marginLeft: 4,
    },
    input: {
        backgroundColor: '#F9FAFB',
        borderWidth: 1.5,
        borderColor: '#F3F4F6',
        borderRadius: 16,
        padding: 14,
        fontSize: 16,
        color: '#111827',
        marginBottom: 20,
    },
    textArea: {
        height: 100,
        textAlignVertical: 'top',
    },
    pickerContainer: {
        backgroundColor: '#F9FAFB',
        borderWidth: 1.5,
        borderColor: '#F3F4F6',
        borderRadius: 16,
        marginBottom: 20,
        overflow: 'hidden',
    },
    picker: {
        height: 55,
        width: '100%',
    },
    dateTimeRow: {
        flexDirection: 'row',
        gap: 12,
        marginBottom: 20,
    },
    dateTimeButton: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F9FAFB',
        borderWidth: 1.5,
        borderColor: '#F3F4F6',
        borderRadius: 16,
        padding: 14,
        gap: 10,
    },
    dateTimeButtonText: {
        fontSize: 15,
        color: '#111827',
        fontWeight: '600',
    },
    submitButton: {
        backgroundColor: MENTOR_PRIMARY,
        paddingVertical: 18,
        borderRadius: 20,
        alignItems: 'center',
        shadowColor: MENTOR_PRIMARY,
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.3,
        shadowRadius: 12,
        elevation: 10,
        marginBottom: Platform.OS === 'ios' ? 20 : 0,
    },
    submitButtonText: {
        color: '#fff',
        fontSize: 17,
        fontWeight: '800',
    },
});

export default MentorScheduleModal;
